/**
 * RiskSegmentOverlay draws the result of a route risk assessment on the map: one coloured Polyline per
 * segment (band colour, dashed when the data coverage is low), white circle markers for mandatory
 * driver stops (green ring = a PSR is in reach, red ring = none) and red triangles for incident
 * hotspots. A shared, non-interactive tooltip follows the mouse, mirroring the hover overlay pattern in
 * SafetyRouting/RouteRenderManager.
 *
 * It sits above the DirectionsRenderer line (zIndex 1100+) and never touches the renderer itself, so
 * bookmarking, the radius filter and the export keep reading the untouched standard route.
 */

/* eslint-disable no-undef */

import { LOW_COVERAGE, bandFor, escapeHtml, reasonLabel, segmentNumber } from "./RiskFormat";
import { cumulativeDistances, slicePathByMetres, pointAtMetres } from "./RiskGeometry";
import { HoverTooltip } from "../HoverTooltip";

const WEIGHT_NORMAL = 8;
const WEIGHT_HIGHLIGHT = 12;
const DASH_WEIGHT_NORMAL = 6;
const DASH_WEIGHT_HIGHLIGHT = 9;

export class RiskSegmentOverlay {
  /**
   * @param {google.maps.Map} map
   */
  constructor(map) {
    this.map = map;
    /** @type {Array<{segmentId: string, polyline: google.maps.Polyline, dashed: boolean, color: string}>} */
    this.segments = [];
    /** @type {google.maps.Marker[]} */
    this.markers = [];
    this.tooltip = new HoverTooltip();
    this.activeHotspot = null;
    this.onSelect = null;
  }

  /**
   * Draws the assessment on `path` — the detailed route path the engine was given (see
   * RiskAssessmentController._detailedPathOf), so the km offsets of segments, stops and hotspots
   * land where the engine measured them. A google.maps.DirectionsRoute is still accepted and falls
   * back to its (simplified) overview_path.
   *
   * @param {object} kpi The KPI object from the backend (kpi.segments[] with fromKm/toKm etc.).
   * @param {google.maps.LatLng[]|google.maps.DirectionsRoute} pathOrRoute
   * @param {Array<object>} bands
   * @param {{onSelect?: (segmentId: string) => void}} [callbacks]
   */
  render(kpi, pathOrRoute, bands, callbacks = {}) {
    this.clear();
    this.onSelect = typeof callbacks.onSelect === "function" ? callbacks.onSelect : null;

    const path = Array.isArray(pathOrRoute) ? pathOrRoute : pathOrRoute && pathOrRoute.overview_path;
    if (!kpi || !Array.isArray(kpi.segments) || !path || path.length < 2) {
      return;
    }
    const cum = cumulativeDistances(path);

    kpi.segments.forEach((segment, index) => {
      this._drawSegment(segment, index, path, cum, bands);
      (segment.mandatoryStops || []).forEach((stop) => this._drawMandatoryStop(stop, path, cum));
      (segment.hotspots || []).forEach((hotspot) => this._drawHotspot(hotspot, path, cum));
    });
  }

  _drawSegment(segment, index, path, cum, bands) {
    const band = bandFor(segment.segmentScore, bands, segment.band);
    const color = band.color;
    const dashed = Number.isFinite(Number(segment.coverageFactor)) && Number(segment.coverageFactor) < LOW_COVERAGE;
    const slice = slicePathByMetres(path, cum, Number(segment.fromKm) * 1000, Number(segment.toKm) * 1000);

    const options = {
      path: slice,
      map: this.map,
      strokeColor: color,
      strokeWeight: WEIGHT_NORMAL,
      strokeOpacity: 0.9,
      zIndex: 1100,
      clickable: true,
    };
    if (dashed) {
      options.strokeOpacity = 0;
      options.icons = [
        {
          icon: { path: "M 0,-1 0,1", strokeOpacity: 0.9, strokeColor: color, strokeWeight: DASH_WEIGHT_NORMAL, scale: 3 },
          offset: "0",
          repeat: "16px",
        },
      ];
    }
    const polyline = new google.maps.Polyline(options);
    const html = this._segmentTooltip(segment, index, band);

    polyline.addListener("mouseover", (event) => {
      if (this.activeHotspot) {
        return;
      }
      this.tooltip.setContent(html);
      this.tooltip.setPosition(event.latLng);
      this.tooltip.open(this.map);
    });
    polyline.addListener("mousemove", (event) => {
      if (!this.activeHotspot) {
        this.tooltip.setPosition(event.latLng);
      }
    });
    polyline.addListener("mouseout", () => {
      if (!this.activeHotspot) {
        this.tooltip.close();
      }
    });
    polyline.addListener("click", () => {
      this.tooltip.close();
      this.highlight(segment.segmentId);
      if (this.onSelect) {
        this.onSelect(segment.segmentId);
      }
    });

    this.segments.push({ segmentId: segment.segmentId, polyline, dashed, color });
  }

  _segmentTooltip(segment, index, band) {
    const n = segmentNumber(segment.segmentId, index + 1);
    const parts = [
      `Segment ${n}`,
      `km ${Math.round(Number(segment.fromKm))}–${Math.round(Number(segment.toKm))}`,
      `${band.name} (${Math.round(Number(segment.segmentScore))})`,
      segment.confidence && segment.confidence.level ? `${segment.confidence.level} confidence` : null,
      Number.isFinite(Number(segment.coverageFactor)) ? `coverage ${Number(segment.coverageFactor).toFixed(2)}` : null,
      segment.isMandatoryStop ? "mandatory stop" : null,
    ].filter(Boolean);
    return `<div style="line-height:1.4;">${parts.map(escapeHtml).join(" · ")}</div>`;
  }

  _drawMandatoryStop(stop, path, cum) {
    const position = pointAtMetres(path, cum, Number(stop.atKm) * 1000);
    if (!position) {
      return;
    }
    const hasPsr = stop.nearestPsrM !== null && stop.nearestPsrM !== undefined;
    const psrText = hasPsr ? `nearest PSR ${(Number(stop.nearestPsrM) / 1000).toFixed(1)} km` : "no PSR in reach";
    const title = `Mandatory stop (${reasonLabel(stop.reason)}) at km ${Math.round(Number(stop.atKm))} – ${psrText}${
      stop.inHotspot ? " – in hotspot" : ""
    }`;
    const marker = new google.maps.Marker({
      position,
      map: this.map,
      title,
      zIndex: 1200,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#fff",
        fillOpacity: 1,
        strokeWeight: 3,
        strokeColor: hasPsr ? "#28a745" : "#dc3545",
      },
    });
    this.markers.push(marker);
  }

  _drawHotspot(hotspot, path, cum) {
    let position = null;
    if (Number.isFinite(Number(hotspot.lat)) && Number.isFinite(Number(hotspot.lng))) {
      position = new google.maps.LatLng(Number(hotspot.lat), Number(hotspot.lng));
    } else if (hotspot.atKm !== undefined && hotspot.atKm !== null) {
      position = pointAtMetres(path, cum, Number(hotspot.atKm) * 1000);
    }
    if (!position) {
      return;
    }
    const share = Number.isFinite(Number(hotspot.share)) ? ` (${Math.round(Number(hotspot.share) * 100)} %)` : "";
    const title = `Hotspot: ${Number(hotspot.incidentCount) || 0} incidents${share}`;
    const marker = new google.maps.Marker({
      position,
      map: this.map,
      title,
      zIndex: 1150,
      icon: {
        path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
        scale: 6,
        fillColor: "#dc3545",
        fillOpacity: 0.95,
        strokeWeight: 1,
        strokeColor: "#ffffff",
      },
    });
    marker.addListener("mouseover", () => {
      this.activeHotspot = marker;
      this.tooltip.setContent(`<div style="line-height:1.4;">${escapeHtml(title)}</div>`);
      this.tooltip.setPosition(position);
      this.tooltip.open(this.map);
    });
    marker.addListener("mouseout", () => {
      if (this.activeHotspot === marker) {
        this.activeHotspot = null;
        this.tooltip.close();
      }
    });
    this.markers.push(marker);
  }

  /**
   * Emphasises one segment polyline and resets all others. Pass null to reset only.
   * @param {string|null} segmentId
   */
  highlight(segmentId) {
    this.segments.forEach((entry) => {
      const active = entry.segmentId === segmentId;
      if (entry.dashed) {
        entry.polyline.setOptions({
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 0.9,
                strokeColor: entry.color,
                strokeWeight: active ? DASH_WEIGHT_HIGHLIGHT : DASH_WEIGHT_NORMAL,
                scale: 3,
              },
              offset: "0",
              repeat: "16px",
            },
          ],
          zIndex: active ? 1110 : 1100,
        });
      } else {
        entry.polyline.setOptions({
          strokeWeight: active ? WEIGHT_HIGHLIGHT : WEIGHT_NORMAL,
          zIndex: active ? 1110 : 1100,
        });
      }
    });
  }

  /**
   * Removes all overlay objects from the map.
   */
  clear() {
    this.activeHotspot = null;
    this.tooltip.close();
    this.segments.forEach((entry) => entry.polyline.setMap(null));
    this.markers.forEach((marker) => marker.setMap(null));
    this.segments = [];
    this.markers = [];
  }
}
